import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowLeft, Save, Upload, Trash2, Loader2, Plus, X, RotateCcw, Image as ImageIcon, Ban } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { api, formatApiError } from "../lib/api";

const DASH_CODE_KEY = "dash_code";
const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const absUrl = (path) => (path && path.startsWith("/") ? `${API_BASE}${path}` : path);

/** Compress image client-side to keep uploads small. Returns raw base64 (no prefix) + mime. */
const compressImage = (file, maxDim = 900, quality = 0.82) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        const base64 = dataUrl.split(",")[1];
        resolve({ image_base64: base64, mime: "image/jpeg" });
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const dollars = (cents) => (typeof cents === "number" ? (cents / 100).toFixed(2) : "0.00");
const centsFromInput = (v) => {
  const n = Number(String(v).replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.round(n * 100));
};

function Gate({ onOk }) {
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/dashboard/verify", { code });
      if (data.ok) { localStorage.setItem(DASH_CODE_KEY, code); onOk(); }
      else { setErr("Wrong code."); setCode(""); }
    } catch (_) { setErr("Something went wrong."); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6">
      <form onSubmit={submit} data-testid="menu-gate" className="w-full max-w-xs border border-neon-green/40 bg-elevated p-8 text-center">
        <Lock size={28} className="text-neon-green mx-auto" />
        <h1 className="font-heading text-3xl text-neon-green uppercase tracking-wide mt-3">Menu Editor</h1>
        <p className="font-body text-sm text-neutral-400 mt-1">Enter your access code.</p>
        <input
          autoFocus type="tel" inputMode="numeric" value={code}
          onChange={(e) => { setCode(e.target.value.replace(/\D/g, "")); setErr(""); }}
          data-testid="menu-code-input" placeholder="••••••"
          className="mt-5 w-full bg-black border border-neon-green/30 text-white text-center text-2xl tracking-[0.4em] py-3 focus:outline-none focus:border-neon-green"
        />
        {err && <p className="font-body text-sm text-red-500 mt-2" data-testid="menu-code-error">{err}</p>}
        <Button type="submit" disabled={loading} data-testid="menu-code-submit" className="mt-5 w-full font-heading text-xl bg-neon-green text-black hover:bg-neon-yellow py-5">
          {loading ? <Loader2 className="animate-spin" size={20} /> : "Enter"}
        </Button>
      </form>
    </div>
  );
}

const ChipEditor = ({ label, items, onChange, placeholder, testid }) => {
  const [val, setVal] = useState("");
  const add = () => {
    const t = val.trim();
    if (!t) return;
    if (items.includes(t)) { toast.info("Already in the list"); return; }
    onChange([...items, t]); setVal("");
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="border border-neon-green/20 bg-elevated p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-xl text-neon-yellow uppercase tracking-wide">{label}</h3>
        <span className="font-body text-xs text-neutral-500">{items.length} option{items.length === 1 ? "" : "s"}</span>
      </div>
      <div className="flex flex-wrap gap-2 mb-3" data-testid={testid}>
        {items.map((t, i) => (
          <span key={t + i} className="inline-flex items-center gap-2 border border-neon-green/40 bg-black text-white px-3 py-1.5 font-body text-sm">
            {t}
            <button type="button" onClick={() => remove(i)} data-testid={`${testid}-remove-${i}`} className="text-neutral-500 hover:text-red-500"><X size={14} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          data-testid={`${testid}-input`}
          className="bg-black border-neon-green/30 text-white"
        />
        <Button onClick={add} type="button" data-testid={`${testid}-add`} className="bg-neon-green text-black hover:bg-neon-yellow font-heading text-lg px-4">
          <Plus size={16} /> Add
        </Button>
      </div>
    </div>
  );
};

const ImageCell = ({ itemId, currentUrl, onChanged }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(absUrl(currentUrl) || "");

  useEffect(() => { setPreview(absUrl(currentUrl) || ""); }, [currentUrl]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const { image_base64, mime } = await compressImage(file);
      await api.post("/dashboard/menu/image", { item_id: itemId, image_base64, mime });
      setPreview(`${API_BASE}/api/menu/image/${itemId}?t=${Date.now()}`);
      toast.success("Image saved");
      onChanged && onChanged(itemId);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/dashboard/menu/image/${itemId}`);
      setPreview("");
      toast.success("Image removed");
      onChanged && onChanged(itemId, true);
    } catch (err) {
      toast.error("Could not remove image");
    } finally { setBusy(false); }
  };

  return (
    <div className="w-20 h-20 shrink-0 relative border border-neon-green/25 bg-black flex items-center justify-center overflow-hidden">
      {preview ? (
        <img src={preview} alt="" className="w-full h-full object-cover" data-testid={`item-image-${itemId}`} />
      ) : (
        <ImageIcon size={22} className="text-neutral-700" />
      )}
      {busy && (
        <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
          <Loader2 className="animate-spin text-neon-green" size={20} />
        </div>
      )}
      <input ref={ref} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid={`image-file-${itemId}`} />
      <div className="absolute -bottom-2 -right-2 flex gap-1">
        <button onClick={() => ref.current?.click()} title="Upload" data-testid={`image-upload-${itemId}`} className="bg-neon-green text-black p-1 hover:bg-neon-yellow">
          <Upload size={12} />
        </button>
        {preview && (
          <button onClick={remove} title="Remove" data-testid={`image-remove-${itemId}`} className="bg-black border border-red-500 text-red-500 p-1 hover:bg-red-500 hover:text-black">
            <Trash2 size={12} />
          </button>
        )}
      </div>
    </div>
  );
};

const ItemRow = ({ catKey, index, item, images, onChange, onDelete, refreshImages }) => {
  const set = (patch) => onChange(catKey, index, { ...item, ...patch });
  const setVariant = (vi, patch) => {
    const variants = [...(item.variants || [])];
    variants[vi] = { ...variants[vi], ...patch };
    onChange(catKey, index, { ...item, variants });
  };
  const enabled = item.enabled !== false;
  const soldOut = !!item.sold_out;
  const confirmDelete = () => {
    if (window.confirm(`Delete "${item.name || item.id}" from the menu? This cannot be undone.`)) {
      onDelete(catKey, index);
    }
  };
  return (
    <div className={`border bg-elevated p-4 transition-colors ${soldOut ? "border-neon-yellow/40 bg-neon-yellow/[0.03]" : "border-neon-green/20"}`} data-testid={`edit-item-${item.id}`}>
      <div className="flex items-start gap-4">
        <ImageCell itemId={item.id} currentUrl={images[item.id]} onChanged={refreshImages} />
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center gap-3 flex-wrap">
            <Input
              value={item.name || ""}
              onChange={(e) => set({ name: e.target.value })}
              className="flex-1 min-w-[180px] bg-black border-neon-green/30 text-white font-heading text-lg"
              data-testid={`item-name-${item.id}`}
            />
            {soldOut && (
              <span className="inline-flex items-center gap-1 border border-neon-yellow bg-neon-yellow/10 text-neon-yellow px-2 py-0.5 font-heading text-xs uppercase tracking-widest">
                <Ban size={11} /> Sold out
              </span>
            )}
            <label className="flex items-center gap-2 text-neutral-400 text-xs font-heading uppercase tracking-widest">
              <Switch checked={soldOut} onCheckedChange={(v) => set({ sold_out: v })} data-testid={`item-soldout-${item.id}`} />
              <span className={soldOut ? "text-neon-yellow" : "text-neutral-500"}>86</span>
            </label>
            <label className="flex items-center gap-2 text-neutral-400 text-xs font-heading uppercase tracking-widest">
              <Switch checked={enabled} onCheckedChange={(v) => set({ enabled: v })} data-testid={`item-enabled-${item.id}`} />
              {enabled ? <span className="text-neon-green">ON</span> : <span className="text-neutral-500">OFF</span>}
            </label>
            <button
              onClick={confirmDelete}
              data-testid={`item-delete-${item.id}`}
              title="Delete this item"
              className="p-1.5 border border-red-500/40 text-red-500 hover:bg-red-500 hover:text-black transition-colors"
            >
              <Trash2 size={14} />
            </button>
          </div>
          <Textarea
            value={item.desc || ""}
            onChange={(e) => set({ desc: e.target.value })}
            rows={2}
            className="bg-black border-neon-green/30 text-white text-sm resize-none"
            data-testid={`item-desc-${item.id}`}
          />
          {item.variants ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {item.variants.map((v, vi) => (
                <div key={vi} className="border border-neon-green/15 p-2">
                  <label className="font-body text-[11px] text-neutral-500 uppercase tracking-widest block">{v.label}</label>
                  <div className="flex items-center gap-1 mt-1">
                    <span className="text-neon-yellow font-heading">$</span>
                    <Input
                      value={dollars(v.price)}
                      onChange={(e) => setVariant(vi, { price: centsFromInput(e.target.value) })}
                      className="bg-black border-neon-green/30 text-white text-right"
                      inputMode="decimal"
                      data-testid={`variant-price-${item.id}-${vi}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-2 max-w-[180px]">
              <span className="text-neon-yellow font-heading text-lg">$</span>
              <Input
                value={dollars(item.price)}
                onChange={(e) => set({ price: centsFromInput(e.target.value) })}
                className="bg-black border-neon-green/30 text-white text-right"
                inputMode="decimal"
                data-testid={`item-price-${item.id}`}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const slugify = (s) =>
  String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40) || `item-${Date.now().toString(36)}`;

function AddItemDialog({ open, catKey, catTitle, onClose, onCreate }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [price, setPrice] = useState("");
  const [hasVariants, setHasVariants] = useState(false);
  const [variants, setVariants] = useState([{ label: "", price: "", flavors: 0 }]);
  const [flavorPick, setFlavorPick] = useState(0);
  const [sodaPick, setSodaPick] = useState(0);
  const [saucePick, setSaucePick] = useState(0);
  const [comboAvailable, setComboAvailable] = useState(false);

  useEffect(() => {
    if (open) {
      setName(""); setDesc(""); setPrice("");
      setHasVariants(false); setVariants([{ label: "", price: "", flavors: 0 }]);
      setFlavorPick(0); setSodaPick(0); setSaucePick(0); setComboAvailable(false);
    }
  }, [open]);

  const addVariantRow = () => setVariants((vs) => [...vs, { label: "", price: "", flavors: 0 }]);
  const removeVariantRow = (i) => setVariants((vs) => vs.filter((_, idx) => idx !== i));
  const setVariantField = (i, patch) => setVariants((vs) => vs.map((v, idx) => (idx === i ? { ...v, ...patch } : v)));

  const canSubmit = (() => {
    if (!name.trim()) return false;
    if (hasVariants) {
      const valid = variants.filter((v) => v.label.trim() && centsFromInput(v.price) > 0);
      return valid.length >= 1;
    }
    return centsFromInput(price) > 0;
  })();

  const submit = () => {
    const base = {
      id: slugify(name),
      name: name.trim(),
      desc: desc.trim() || undefined,
      enabled: true,
      sold_out: false,
    };
    if (flavorPick > 0) base.flavorPick = flavorPick;
    if (sodaPick > 0) base.sodaPick = sodaPick;
    if (saucePick > 0) base.saucePick = saucePick;
    if (hasVariants) {
      base.type = "protein";
      base.comboAvailable = comboAvailable;
      base.variants = variants
        .filter((v) => v.label.trim() && centsFromInput(v.price) > 0)
        .map((v) => ({
          label: v.label.trim(),
          price: centsFromInput(v.price),
          ...(Number(v.flavors) > 0 ? { flavors: Number(v.flavors) } : {}),
        }));
    } else {
      base.type = "addon";
      base.price = centsFromInput(price);
    }
    onCreate(base);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-elevated border-neon-green/30 text-white max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl text-neon-green uppercase tracking-wide">
            Add Item {catTitle ? <span className="text-neon-yellow">· {catTitle}</span> : null}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-400">Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Chicken Nuggets"
              data-testid="add-item-name"
              className="bg-black border-neon-green/30 text-white font-heading text-lg mt-1"
              autoFocus
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-400">Short description (optional)</Label>
            <Textarea
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="e.g. Golden & crispy, served with your pick of sauce"
              rows={2}
              data-testid="add-item-desc"
              className="bg-black border-neon-green/30 text-white text-sm resize-none mt-1"
            />
          </div>

          <div className="flex items-center gap-3 border border-neon-green/15 bg-black p-3">
            <Switch checked={hasVariants} onCheckedChange={setHasVariants} data-testid="add-item-has-variants" />
            <div className="flex-1">
              <div className="font-heading text-sm text-white uppercase tracking-wide">Multiple sizes / variants</div>
              <div className="font-body text-xs text-neutral-500">Turn on for items like "6 Piece / 10 Piece". Leave off for one-price items.</div>
            </div>
          </div>

          {!hasVariants ? (
            <div>
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Price</Label>
              <div className="flex items-center gap-2 mt-1 max-w-[180px]">
                <span className="text-neon-yellow font-heading text-xl">$</span>
                <Input
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="0.00"
                  inputMode="decimal"
                  data-testid="add-item-price"
                  className="bg-black border-neon-green/30 text-white text-right text-lg"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Sizes</Label>
              {variants.map((v, i) => (
                <div key={i} className="grid grid-cols-[1fr_110px_90px_auto] gap-2 items-center">
                  <Input
                    value={v.label}
                    onChange={(e) => setVariantField(i, { label: e.target.value })}
                    placeholder="e.g. 6 Piece"
                    data-testid={`add-variant-label-${i}`}
                    className="bg-black border-neon-green/30 text-white"
                  />
                  <div className="flex items-center gap-1">
                    <span className="text-neon-yellow font-heading">$</span>
                    <Input
                      value={v.price}
                      onChange={(e) => setVariantField(i, { price: e.target.value })}
                      placeholder="0.00"
                      inputMode="decimal"
                      data-testid={`add-variant-price-${i}`}
                      className="bg-black border-neon-green/30 text-white text-right"
                    />
                  </div>
                  <Input
                    value={v.flavors}
                    onChange={(e) => setVariantField(i, { flavors: e.target.value.replace(/\D/g, "") })}
                    placeholder="Flavors"
                    inputMode="numeric"
                    data-testid={`add-variant-flavors-${i}`}
                    className="bg-black border-neon-green/30 text-white text-center"
                    title="How many flavors to pick for this size"
                  />
                  <button
                    onClick={() => removeVariantRow(i)}
                    disabled={variants.length <= 1}
                    className="p-2 text-red-500 disabled:opacity-30 hover:bg-red-500/10"
                    data-testid={`add-variant-remove-${i}`}
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
              <Button onClick={addVariantRow} variant="outline" data-testid="add-variant-row" className="border-neon-green/30 text-neon-green hover:bg-neon-green/10">
                <Plus size={14} className="mr-1" /> Another size
              </Button>
              <label className="flex items-center gap-2 pt-1">
                <Switch checked={comboAvailable} onCheckedChange={setComboAvailable} data-testid="add-item-combo" />
                <span className="font-body text-xs text-neutral-400">Combo available (adds fries & soda for the combo price)</span>
              </label>
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-400">Customer picks</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              <div>
                <div className="font-body text-[10px] text-neutral-500 uppercase tracking-widest">Flavors</div>
                <Input
                  value={flavorPick}
                  onChange={(e) => setFlavorPick(Number(e.target.value.replace(/\D/g, "")) || 0)}
                  inputMode="numeric"
                  data-testid="add-item-flavor-pick"
                  className="bg-black border-neon-green/30 text-white text-center"
                />
              </div>
              <div>
                <div className="font-body text-[10px] text-neutral-500 uppercase tracking-widest">Sodas</div>
                <Input
                  value={sodaPick}
                  onChange={(e) => setSodaPick(Number(e.target.value.replace(/\D/g, "")) || 0)}
                  inputMode="numeric"
                  data-testid="add-item-soda-pick"
                  className="bg-black border-neon-green/30 text-white text-center"
                />
              </div>
              <div>
                <div className="font-body text-[10px] text-neutral-500 uppercase tracking-widest">Sauces</div>
                <Input
                  value={saucePick}
                  onChange={(e) => setSaucePick(Number(e.target.value.replace(/\D/g, "")) || 0)}
                  inputMode="numeric"
                  data-testid="add-item-sauce-pick"
                  className="bg-black border-neon-green/30 text-white text-center"
                />
              </div>
            </div>
            <p className="font-body text-[11px] text-neutral-500 mt-1">Leave at 0 if the customer doesn't pick anything (e.g. a plain soda).</p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="add-item-cancel" className="border-neutral-700 text-neutral-400 hover:text-white hover:bg-neutral-900">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            data-testid="add-item-create"
            className="bg-neon-green text-black hover:bg-neon-yellow font-heading disabled:opacity-40"
          >
            <Plus size={16} className="mr-1" /> Add to Menu
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function DashboardMenu() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(!!localStorage.getItem(DASH_CODE_KEY));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [menu, setMenu] = useState({});
  const [order, setOrder] = useState([]);
  const [flavors, setFlavors] = useState([]);
  const [sodas, setSodas] = useState([]);
  const [dips, setDips] = useState([]);
  const [comboPrice, setComboPrice] = useState(400);
  const [images, setImages] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/menu");
      setMenu(data.menu || {});
      setOrder(data.categoryOrder || []);
      setFlavors(data.flavors || []);
      setSodas(data.sodas || []);
      setDips(data.dips || []);
      setComboPrice(data.comboPrice || 400);
      setImages(data.images || {});
    } catch (_) {
      toast.error("Could not load menu");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (authed) load(); }, [authed, load]);

  const updateItem = (catKey, index, next) => {
    setMenu((m) => {
      const cat = m[catKey]; if (!cat) return m;
      const items = [...cat.items]; items[index] = next;
      return { ...m, [catKey]: { ...cat, items } };
    });
  };

  const deleteItem = (catKey, index) => {
    setMenu((m) => {
      const cat = m[catKey]; if (!cat) return m;
      const items = cat.items.filter((_, i) => i !== index);
      return { ...m, [catKey]: { ...cat, items } };
    });
    toast.success("Item removed — click Save to make it live");
  };

  const addItem = (catKey, newItem) => {
    setMenu((m) => {
      const cat = m[catKey] || { title: catKey, items: [] };
      // Ensure unique id
      const existing = new Set((cat.items || []).map((i) => i.id));
      let id = newItem.id;
      if (existing.has(id)) {
        let n = 2;
        while (existing.has(`${id}-${n}`)) n++;
        id = `${id}-${n}`;
      }
      const items = [...(cat.items || []), { ...newItem, id }];
      return { ...m, [catKey]: { ...cat, items } };
    });
    toast.success("Item added — click Save to make it live");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/dashboard/menu", {
        menu, categoryOrder: order, flavors, sodas, dips, comboPrice,
      });
      toast.success("Menu saved — live now");
    } catch (err) {
      if (err.response?.status === 401) { localStorage.removeItem(DASH_CODE_KEY); setAuthed(false); }
      else toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const resetAll = async () => {
    if (!window.confirm("Reset menu to defaults? This does not delete uploaded images.")) return;
    try {
      await api.post("/dashboard/menu/reset");
      toast.success("Reset to defaults");
      load();
    } catch (_) { toast.error("Reset failed"); }
  };

  const [addDialogCat, setAddDialogCat] = useState(null);

  if (!authed) return <Gate onOk={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-neon-green/20 px-5 sm:px-8 h-[64px] flex items-center justify-between sticky top-0 bg-black/95 backdrop-blur-xl z-40">
        <button onClick={() => navigate("/dashboard")} data-testid="menu-back" className="flex items-center gap-2 text-neutral-300 hover:text-neon-green transition-colors">
          <ArrowLeft size={20} /> <span className="font-heading text-xl uppercase">Books</span>
        </button>
        <span className="font-heading text-2xl text-neon-green hidden sm:inline">67 <span className="text-neon-yellow">MENU</span></span>
        <div className="flex items-center gap-2">
          <Button onClick={resetAll} variant="outline" data-testid="menu-reset-btn" className="border-neutral-700 text-neutral-400 hover:bg-neutral-900 hover:text-white">
            <RotateCcw size={16} className="mr-1" /> Reset
          </Button>
          <Button onClick={save} disabled={saving} data-testid="menu-save-btn" className="bg-neon-green text-black hover:bg-neon-yellow font-heading text-lg">
            {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />} Save
          </Button>
        </div>
      </header>

      <div className="mx-auto max-w-[1100px] px-5 sm:px-8 py-8 space-y-8">
        <div>
          <h1 className="font-heading text-5xl sm:text-6xl text-neon-green uppercase tracking-tighter leading-none neon-glow-green">Edit Menu</h1>
          <p className="font-body text-sm text-neutral-500 mt-1">Change prices, flavors & photos — updates go live instantly on 67wings.com.</p>
        </div>

        {loading && !order.length ? (
          <div className="py-20 text-center text-neutral-500"><Loader2 className="animate-spin mx-auto" /></div>
        ) : (
          <>
            <section className="grid gap-4 md:grid-cols-3">
              <ChipEditor label="Flavors" items={flavors} onChange={setFlavors} placeholder="e.g. Hot Cheeto" testid="flavors-editor" />
              <ChipEditor label="Sodas" items={sodas} onChange={setSodas} placeholder="e.g. Fanta" testid="sodas-editor" />
              <ChipEditor label="Dips / Sauces" items={dips} onChange={setDips} placeholder="e.g. Ranch" testid="dips-editor" />
            </section>

            <section className="border border-neon-green/20 bg-elevated p-5 flex items-center gap-4">
              <div className="flex-1">
                <div className="font-heading text-xl text-neon-yellow uppercase tracking-wide">Combo Upcharge</div>
                <div className="font-body text-xs text-neutral-500">Extra charge when a customer makes any wing / tender order a combo.</div>
              </div>
              <div className="flex items-center gap-2 max-w-[160px]">
                <span className="text-neon-yellow font-heading text-xl">$</span>
                <Input
                  value={dollars(comboPrice)}
                  onChange={(e) => setComboPrice(centsFromInput(e.target.value))}
                  className="bg-black border-neon-green/30 text-white text-right text-lg"
                  inputMode="decimal"
                  data-testid="combo-price-input"
                />
              </div>
            </section>

            {order.map((catKey) => {
              const cat = menu[catKey]; if (!cat) return null;
              return (
                <section key={catKey} data-testid={`edit-category-${catKey}`}>
                  <div className="flex items-end justify-between flex-wrap gap-2">
                    <h2 className="font-heading text-3xl text-neon-yellow uppercase tracking-tighter">{cat.title}</h2>
                    <Button
                      onClick={() => setAddDialogCat(catKey)}
                      data-testid={`add-item-btn-${catKey}`}
                      className="bg-neon-green/10 border border-neon-green text-neon-green hover:bg-neon-green hover:text-black font-heading text-sm uppercase tracking-widest"
                    >
                      <Plus size={14} className="mr-1" /> Add Item
                    </Button>
                  </div>
                  <div className="mt-3 space-y-3">
                    {(cat.items || []).map((it, idx) => (
                      <ItemRow
                        key={it.id}
                        catKey={catKey}
                        index={idx}
                        item={it}
                        images={images}
                        onChange={updateItem}
                        onDelete={deleteItem}
                        refreshImages={(itemId, removed) => setImages((prev) => {
                          const next = { ...prev };
                          if (removed) delete next[itemId];
                          else next[itemId] = `/api/menu/image/${itemId}?t=${Date.now()}`;
                          return next;
                        })}
                      />
                    ))}
                    {(cat.items || []).length === 0 && (
                      <div className="border border-dashed border-neon-green/20 p-6 text-center text-neutral-500 font-body text-sm">
                        No items yet — tap "Add Item" to create one.
                      </div>
                    )}
                  </div>
                </section>
              );
            })}

            <AddItemDialog
              open={!!addDialogCat}
              catKey={addDialogCat}
              catTitle={addDialogCat ? (menu[addDialogCat]?.title || addDialogCat) : ""}
              onClose={() => setAddDialogCat(null)}
              onCreate={(newItem) => { addItem(addDialogCat, newItem); setAddDialogCat(null); }}
            />

            <div className="sticky bottom-4 flex justify-end">
              <Button onClick={save} disabled={saving} data-testid="menu-save-btn-bottom" className="bg-neon-green text-black hover:bg-neon-yellow font-heading text-xl px-8 py-6 shadow-[0_0_24px_rgba(57,255,20,0.5)]">
                {saving ? <Loader2 className="animate-spin mr-2" /> : <Save size={18} className="mr-2" />} Save All Changes
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
